import React, { useState, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import useStore from '../stores/useStore';
import { apiFetch } from '../lib/api';

const DEMOS = [
    {
        id: 'pos',
        title: 'Server POS',
        icon: '🍽️',
        description: 'Full point-of-sale with table map and ordering',
        gradient: 'from-blue-600 to-blue-800',
        accent: 'blue'
    },
    {
        id: 'kds',
        title: 'Kitchen Display',
        icon: '👨‍🍳',
        description: 'Real-time order management for expo line',
        gradient: 'from-ember-500 to-ember-700',
        accent: 'ember'
    },
    {
        id: 'bar',
        title: 'Bar Display',
        icon: '🍸',
        description: 'Drink orders for bartenders',
        gradient: 'from-wine-500 to-wine-700',
        accent: 'wine'
    },
    {
        id: 'host',
        title: 'Host Station',
        icon: '📋',
        description: 'Reservations, waitlist & guest profiles',
        gradient: 'from-slate-600 to-slate-800',
        accent: 'slate'
    },
    {
        id: 'manager',
        title: 'Manager Hub',
        icon: '📊',
        description: 'Live metrics and labor analytics',
        gradient: 'from-emerald-600 to-emerald-800',
        accent: 'emerald'
    },
    {
        id: 'timeclock',
        title: 'Time Clock',
        icon: '⏰',
        description: 'Employee punch with Paycom sync',
        gradient: 'from-sage-500 to-sage-600',
        accent: 'sage'
    },
    {
        id: 'tips',
        title: 'Tip Viewer',
        icon: '💵',
        description: 'Earnings & instant Branch payouts',
        gradient: 'from-gold-500 to-gold-600',
        accent: 'gold'
    },
    {
        id: 'inventory',
        title: 'Inventory',
        icon: '📦',
        description: 'Stock tracking, low alerts & auto-reorder',
        gradient: 'from-orange-500 to-orange-700',
        accent: 'orange'
    }
];

const SCENARIOS = [
    {
        id: 'busy-friday',
        name: 'Busy Friday Night',
        description: '15 tables occupied, 8 orders in kitchen, VIP guests',
        icon: '🔥',
        stats: { tables: '15/20', orders: 8, labor: '24%' }
    },
    {
        id: 'smooth-lunch',
        name: 'Smooth Lunch',
        description: '6 tables occupied, light kitchen queue',
        icon: '☀️',
        stats: { tables: '6/20', orders: 2, labor: '18%' }
    }
];

// Animated Flame Logo
const FlameLogo = ({ size = 'lg' }) => {
    const sizeClasses = size === 'lg' ? 'w-16 h-20' : 'w-10 h-12';
    return (
        <svg viewBox="0 0 64 80" className={`${sizeClasses} drop-shadow-lg`} fill="none" role="img" aria-hidden="true">
            <path
                d="M32 0C32 0 8 24 8 48C8 64 18 76 32 80C46 76 56 64 56 48C56 24 32 0 32 0Z"
                fill="url(#flameGradient)"
            />
            <path
                d="M32 20C32 20 18 36 18 52C18 62 24 70 32 72C40 70 46 62 46 52C46 36 32 20 32 20Z"
                fill="url(#innerFlameGradient)"
            />
            <defs>
                <linearGradient id="flameGradient" x1="32" y1="0" x2="32" y2="80" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#F59E0B" />
                    <stop offset="50%" stopColor="#E84A27" />
                    <stop offset="100%" stopColor="#991B1B" />
                </linearGradient>
                <linearGradient id="innerFlameGradient" x1="32" y1="20" x2="32" y2="72" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#FCD34D" />
                    <stop offset="100%" stopColor="#F97316" />
                </linearGradient>
            </defs>
        </svg>
    );
};

export default function DemoLauncher({ onSelectView }) {
    const { resetDemo, notify } = useStore();
    const { isMobile } = useIsMobile();
    const [activeScenario, setActiveScenario] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showScenarios, setShowScenarios] = useState(false);

    useEffect(() => {
        checkCurrentScenario();
    }, []);

    const checkCurrentScenario = async () => {
        try {
            const res = await apiFetch(`/api/demo/scenarios`);
            const data = await res.json();
            if (data.currentScenario) {
                setActiveScenario(data.currentScenario);
            }
        } catch {
            // Server not running
        }
    };

    const handleLoadScenario = async (scenarioId) => {
        setIsLoading(true);
        try {
            const res = await apiFetch(`/api/demo/scenarios/${scenarioId}/load`, {
                method: 'POST'
            });
            const data = await res.json();

            if (data.success) {
                setActiveScenario(scenarioId);
                setShowScenarios(false);
                notify.success(`Loaded: ${data.scenario.name}`);
            }
        } catch {
            notify.error('Failed to load scenario');
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = async () => {
        setIsLoading(true);
        try {
            await apiFetch(`/api/demo/reset`, { method: 'POST' });
            resetDemo();
            setActiveScenario(null);
            notify.success('Demo reset');
        } catch {
            resetDemo();
            setActiveScenario(null);
            notify.info('Local state reset');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <main className="min-h-full bg-smoke-950 text-white overflow-auto noise-overlay" aria-label="EmberPOS Demo Launcher">
            {/* Skip to main content */}
            <a href="#fire-pos-main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:px-4 focus:py-2 focus:bg-ember-600 focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-ember-400">Skip to main content</a>
            {/* Background Effects */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-ember-500/5 rounded-full blur-[120px]" />
                <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-ember-900/10 to-transparent" />
            </div>

            <div id="fire-pos-main" className={`relative z-10 container mx-auto max-w-7xl ${isMobile ? 'px-3 py-4' : 'px-4 py-8 md:py-12'}`}>
                {/* Header */}
                <div className={`text-center ${isMobile ? 'mb-6' : 'mb-12'}`}>
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <FlameLogo />
                        <h1 className={`font-display font-bold tracking-tight ${isMobile ? 'text-3xl' : 'text-5xl md:text-6xl'}`}>
                            <span className="text-gradient-ember">Ember</span>
                            <span className="text-white ml-2">POS</span>
                        </h1>
                    </div>
                    <p className={`text-smoke-400 font-light ${isMobile ? 'text-base mb-4' : 'text-xl mb-6'}`}>Hospitality Operating System</p>

                    {/* Value Props */}
                    <div className="flex flex-wrap justify-center gap-3">
                        {[
                            { icon: '💰', text: '83% Lower Cost', color: 'border-sage-500/40 bg-sage-500/10 text-sage-400' },
                            { icon: '🔌', text: 'Zero API Fees', color: 'border-blue-500/40 bg-blue-500/10 text-blue-400' },
                            { icon: '🤖', text: 'AI Assistant', color: 'border-purple-500/40 bg-purple-500/10 text-purple-400' },
                        ].map((prop, i) => (
                            <span
                                key={i}
                                className={`${prop.color} border px-4 py-2 rounded-full text-sm font-medium
                                           animate-slide-up`}
                                style={{ animationDelay: `${i * 100}ms` }}
                            >
                                {prop.icon} {prop.text}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Scenario Selector */}
                <div className={`max-w-4xl mx-auto ${isMobile ? 'mb-6' : 'mb-10'}`}>
                    <div className="glass-card p-5">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <p className="text-smoke-500 text-sm font-medium uppercase tracking-wide">Demo Scenario</p>
                                <p className="text-xl font-semibold mt-1">
                                    {activeScenario
                                        ? SCENARIOS.find(s => s.id === activeScenario)?.name || 'Custom'
                                        : 'Fresh Start'}
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowScenarios(!showScenarios)}
                                    aria-label="Load demo scenario" className="btn-ember text-sm focus:outline-none focus:ring-2 focus:ring-ember-400"
                                >
                                    🎬 Load Scenario
                                </button>
                                <button
                                    onClick={handleReset}
                                    disabled={isLoading}
                                    aria-label="Reset demo" className="btn-smoke text-sm focus:outline-none focus:ring-2 focus:ring-white"
                                >
                                    ↻ Reset
                                </button>
                            </div>
                        </div>

                        {/* Scenario Options */}
                        {showScenarios && (
                            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 pt-5 border-t border-white/10">
                                {SCENARIOS.map(scenario => (
                                    <button
                                        key={scenario.id}
                                        onClick={() => handleLoadScenario(scenario.id)}
                                        disabled={isLoading}
                                        className={`p-5 rounded-xl border-2 text-left transition-all duration-200 ${
                                            activeScenario === scenario.id
                                                ? 'border-ember-500 bg-ember-500/10'
                                                : 'border-smoke-700 hover:border-smoke-500 hover:bg-smoke-800/50'
                                        }`}
                                    >
                                        <div className="flex items-start gap-4">
                                            <span className="text-4xl">{scenario.icon}</span>
                                            <div>
                                                <p className="font-bold text-lg">{scenario.name}</p>
                                                <p className="text-sm text-smoke-400 mt-1">{scenario.description}</p>
                                                <div className="flex gap-4 mt-3 text-xs text-smoke-500">
                                                    <span className="pill-success">Tables: {scenario.stats.tables}</span>
                                                    <span className="pill-warning">Orders: {scenario.stats.orders}</span>
                                                    <span className="pill-gold">Labor: {scenario.stats.labor}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Demo Cards Grid */}
                <div className={`grid gap-4 max-w-6xl mx-auto ${
                    isMobile ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5'
                }`}>
                    {DEMOS.map((demo, idx) => (
                        <button
                            key={demo.id}
                            onClick={() => onSelectView(demo.id)}
                            aria-label={`Launch ${demo.title}: ${demo.description}`}
                            className="group relative rounded-2xl overflow-hidden transition-all duration-300
                                     hover:scale-[1.02] hover:-translate-y-1 text-left animate-slide-up
                                     focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-smoke-950"
                            style={{ animationDelay: `${idx * 75}ms` }}
                        >
                            {/* Background gradient */}
                            <div className={`absolute inset-0 bg-gradient-to-br ${demo.gradient} opacity-90`} />

                            {/* Shine effect on hover */}
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent
                                              -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                            </div>

                            {/* Content */}
                            <div className={`relative ${isMobile ? 'p-4' : 'p-6'}`}>
                                <div className={`mb-3 transform group-hover:scale-110 transition-transform duration-300 ${isMobile ? 'text-3xl' : 'text-5xl mb-4'}`}>
                                    {demo.icon}
                                </div>
                                <h3 className={`font-bold mb-1 ${isMobile ? 'text-base' : 'text-xl mb-2'}`}>{demo.title}</h3>
                                {!isMobile && <p className="text-white/70 text-sm leading-relaxed">{demo.description}</p>}

                                <div className={`flex items-center text-white/50 text-sm font-medium group-hover:text-white/80 transition-colors ${isMobile ? 'mt-2' : 'mt-5'}`}>
                                    <span>Launch</span>
                                    <span className="ml-2 transform group-hover:translate-x-1 transition-transform">→</span>
                                </div>
                            </div>

                            {/* Border glow on hover */}
                            <div className="absolute inset-0 rounded-2xl border-2 border-white/0 group-hover:border-white/20 transition-colors" />
                        </button>
                    ))}
                </div>

                {/* Tech Stack - hidden on mobile */}
                <div className={`max-w-4xl mx-auto ${isMobile ? 'hidden' : 'mt-16'}`}>
                    <div className="glass-card p-8">
                        <h3 className="text-xl font-display font-bold text-center mb-8 text-smoke-300">Technology Stack</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                            {[
                                { icon: '⚛️', name: 'React 19', sub: 'Vite + Tailwind' },
                                { icon: '🤖', name: 'AI Gateway', sub: 'Hosted (Groq)' },
                                { icon: '🐘', name: 'PostgreSQL', sub: 'Local + VPS' },
                                { icon: '📺', name: 'Fire Tablets', sub: 'Native Android' },
                            ].map((tech, i) => (
                                <div key={i} className="text-center">
                                    <div className="text-4xl mb-3">{tech.icon}</div>
                                    <p className="font-semibold text-white">{tech.name}</p>
                                    <p className="text-xs text-smoke-500 mt-1">{tech.sub}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Integrations - hidden on mobile */}
                <div className={`max-w-4xl mx-auto text-center ${isMobile ? 'hidden' : 'mt-10'}`}>
                    <p className="text-smoke-600 text-sm font-medium uppercase tracking-wide mb-4">Integrations Ready</p>
                    <div className="flex flex-wrap justify-center gap-3">
                        {['💼 Paycom HR', '💳 Amazon Pay', '📅 OpenTable', '💵 Branch Tips', '📚 Canvas LMS'].map((int, i) => (
                            <span key={i} className="bg-smoke-800/50 border border-smoke-700 px-4 py-2 rounded-lg text-sm text-smoke-400">
                                {int}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-16 text-center text-smoke-600 text-sm">
                    <p>EmberPOS v1.0 — Built for independent restaurants</p>
                </div>
            </div>
        </main>
    );
}
